import { useMemo } from 'react';

/** Cálculos compartidos para un bloque Ingresos o Costos y gastos del reporte Resultados. */

export function buildComparativeRowsFromByMonth(byMonth, { warehouse = true } = {}) {
  const rowMap = {};
  byMonth.forEach((block) => {
    block.data.forEach((row) => {
      const key = warehouse ? `${row.product_id}_${row.warehouse_id}` : String(row.product_id);
      if (!rowMap[key]) {
        rowMap[key] = {
          product_id: row.product_id,
          product_name: row.product_name || '-',
          product_sku: row.product_sku || '-',
          warehouse_id: row.warehouse_id,
          warehouse_name: row.warehouse_name || '-',
          months: {},
          machinery_label: row.machinery_label || '',
          category_id: row.category_id,
          category_code: row.category_code || '',
          category_name: row.category_name || 'Sin clasificación',
          subcategory_id: row.subcategory_id,
          subcategory_code: row.subcategory_code || '',
          subcategory_name: row.subcategory_name || '',
          family_id: row.family_id,
          family_code: row.family_code || '',
          family_name: row.family_name || '',
        };
      }
      if (warehouse) {
        rowMap[key].months[block.month_key] = {
          quantity: row.exits_quantity ?? 0,
          amount: row.exits_amount ?? 0,
        };
      } else {
        rowMap[key].months[block.month_key] = {
          quantity: row.quantity ?? 0,
          amount: row.amount_sin_igv ?? 0,
        };
      }
      if (row.machinery_label && String(row.machinery_label).trim() !== '') {
        const current = String(rowMap[key].machinery_label || '').trim();
        const incoming = String(row.machinery_label).trim();
        if (!current) rowMap[key].machinery_label = incoming;
        else if (!current.split(' ; ').includes(incoming)) rowMap[key].machinery_label = `${current} ; ${incoming}`;
      }
    });
  });
  return Object.values(rowMap).filter((row) => {
    const vals = Object.values(row.months);
    if (vals.length === 0) return false;
    return vals.some((m) => m.quantity !== 0 || m.amount !== 0);
  });
}

export function buildHierarchyTree(comparativeRows) {
  const catMap = {};
  comparativeRows.forEach((row) => {
    const cid = row.category_id ?? 0;
    const cname = row.category_name || 'Sin clasificación';
    const ccode = row.category_code || (cid === 0 ? 'SIN-CAT' : '');
    if (!catMap[cid]) {
      catMap[cid] = { id: cid, code: ccode, name: cname, subcategories: {} };
    }
    const subid = row.subcategory_id ?? 0;
    const subname = row.subcategory_name || '';
    const subcode = row.subcategory_code || '';
    if (!catMap[cid].subcategories[subid]) {
      catMap[cid].subcategories[subid] = { id: subid, code: subcode, name: subname, families: {} };
    }
    const fid = row.family_id ?? 0;
    const fname = row.family_name || '';
    const fcode = row.family_code || '';
    if (!catMap[cid].subcategories[subid].families[fid]) {
      catMap[cid].subcategories[subid].families[fid] = { id: fid, code: fcode, name: fname, rows: [] };
    }
    catMap[cid].subcategories[subid].families[fid].rows.push(row);
  });
  const categories = Object.values(catMap).map((c) => ({
    ...c,
    subcategories: Object.values(c.subcategories).map((s) => ({
      ...s,
      families: Object.values(s.families).filter((f) => f.rows.length > 0),
    })),
  }));
  const sortName = (a, b) => (a.name || '').localeCompare(b.name || '', 'es');
  categories.forEach((c) => {
    c.subcategories = c.subcategories.filter((s) => s.families.length > 0);
    c.subcategories.sort(sortName);
    c.subcategories.forEach((s) => s.families.sort(sortName));
  });
  const filtered = categories.filter((c) => c.subcategories.length > 0);
  filtered.sort((a, b) => {
    if (a.name === 'Sin clasificación') return 1;
    if (b.name === 'Sin clasificación') return -1;
    return sortName(a, b);
  });
  return filtered;
}

export function buildDepreciationHierarchy(depreciacionesRows) {
  const catMap = {};
  depreciacionesRows.forEach((row) => {
    const cid = row.category_id ?? 0;
    const cname = row.category_name || 'Sin clasificación';
    const ccode = row.category_code || (cid === 0 ? 'SIN-CAT' : '');
    if (!catMap[cid]) {
      catMap[cid] = { id: cid, code: ccode, name: cname, subcategories: {} };
    }
    const subid = row.subcategory_id ?? 0;
    const subname = row.subcategory_name || '';
    const subcode = row.subcategory_code || '';
    if (!catMap[cid].subcategories[subid]) {
      catMap[cid].subcategories[subid] = { id: subid, code: subcode, name: subname, families: {} };
    }
    const fid = row.family_id ?? 0;
    const fname = row.family_name || '';
    const fcode = row.family_code || '';
    if (!catMap[cid].subcategories[subid].families[fid]) {
      catMap[cid].subcategories[subid].families[fid] = { id: fid, code: fcode, name: fname, rows: [] };
    }
    catMap[cid].subcategories[subid].families[fid].rows.push(row);
  });
  const categories = Object.values(catMap).map((c) => ({
    ...c,
    subcategories: Object.values(c.subcategories).map((s) => ({
      ...s,
      families: Object.values(s.families).filter((f) => f.rows.length > 0),
    })),
  }));
  const sortName = (a, b) => (a.name || '').localeCompare(b.name || '', 'es');
  categories.forEach((c) => {
    c.subcategories = c.subcategories.filter((s) => s.families.length > 0);
    c.subcategories.sort(sortName);
    c.subcategories.forEach((s) => s.families.sort(sortName));
  });
  const filtered = categories.filter((c) => c.subcategories.length > 0);
  filtered.sort((a, b) => {
    if (a.name === 'Sin clasificación') return 1;
    if (b.name === 'Sin clasificación') return -1;
    return sortName(a, b);
  });
  return filtered;
}

export const EMPTY_LIST = [];

export function useResultsSideBundle(sideData, depreciacionesRows = EMPTY_LIST) {
  return useMemo(() => {
    const byMonthSinAlmacen = sideData?.compras_sin_almacen_by_month || [];
    const comparativeRowsSinAlmacen = buildComparativeRowsFromByMonth(byMonthSinAlmacen, { warehouse: false });
    const hierarchyTreeSinAlmacen = buildHierarchyTree(comparativeRowsSinAlmacen);
    const hierarchyTreeDepreciaciones = buildDepreciationHierarchy(depreciacionesRows || EMPTY_LIST);
    return {
      byMonthSinAlmacen,
      hierarchyTreeSinAlmacen,
      hierarchyTreeDepreciaciones,
    };
  }, [sideData, depreciacionesRows]);
}
